"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toStructuredErrors = exports.internalMessageToSerializedStructuredPublicError = exports.generateCSVForStructuredError = exports.getErrorMessage = exports.isFinleyErrorType = exports.isErrorType = exports.finleyAssertUnreachable = exports.finleyAssert = exports.TypeError = exports.Unauthorized = exports.NotFound = exports.Forbidden = exports.CreateError = exports.Conflicts = exports.BadRequest = exports.StructuredPublicErrors = exports.FinleyError = exports.LogLevel = void 0;
// eslint-disable-next-line max-classes-per-file
const assert_1 = __importDefault(require("assert"));
const finley_types_1 = require("@finley-technologies/finley-types");
exports.LogLevel = finley_types_1.Log.LogLevel;
/**
 * Finely generic error
 */
class FinleyError extends Error {
    externalMessage;
    metadata;
    logLevel;
    constructor(message, options) {
        super(message);
        this.metadata = options?.metadata;
        this.externalMessage = options?.externalMessage || message;
        this.logLevel = options?.logLevel;
    }
}
exports.FinleyError = FinleyError;
/** An error that contains multiple StructuredError */
class StructuredPublicErrors extends FinleyError {
    metadata;
    constructor(message, options) {
        const { structuredErrors, metadata, ...rest } = options;
        const superOptions = {
            ...rest,
            metadata,
            externalMessage: `${structuredErrors.length} ${structuredErrors.length === 1 ? 'error' : 'errors'} occurred.`,
        };
        super(message, superOptions);
        this.metadata = { ...metadata, structuredErrors };
    }
}
exports.StructuredPublicErrors = StructuredPublicErrors;
/**
 * BadRequest
 * This error is primarily used in gateway services, where request is validated from external users.
 * Internal service communication validation should throw FinleyError
 */
class BadRequest extends FinleyError {
    constructor({ message, externalMessage, metadata, logLevel, }) {
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.BadRequest = BadRequest;
/**
 * Conflicts
 * This error should be used for exceptions along the line of:
 *  - state transition error due to invalid signal for current state
 *  - mutation action on already immutable records
 */
class Conflicts extends FinleyError {
    constructor({ message, externalMessage, metadata, logLevel, }) {
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.Conflicts = Conflicts;
/**
 * NOTE:
 * We should be able to intercept DB error, and extend
 * its message
 */
class CreateError extends FinleyError {
    constructor({ resourceType, externalMessage, metadata, logLevel, }) {
        const message = `Unable to create resource type [${resourceType}]`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.CreateError = CreateError;
/**
 * Placeholder for unauthorized access (aka, Forbidden)
 * Lookup parameter are no longer required, as some error may not require them.
 * We've also added an optional `action` param, as we may want to know which action was possibly denied.
 */
class Forbidden extends FinleyError {
    constructor({ resourceType, actionType, lookupParam, lookupValue, metadata, logLevel, }) {
        let message = `Not authorized to take action [${actionType}] for resource [${resourceType}].`;
        if (lookupParam || lookupValue) {
            message += ` By param [${lookupParam}] [${lookupValue}]`;
        }
        // const message = `Unable to access resource type [${resourceType}] by param [${lookupParam}] [${lookupValue}]`;
        super(message, { metadata, logLevel });
    }
}
exports.Forbidden = Forbidden;
/**
 * Thrown when specified resource is not found
 */
class NotFound extends FinleyError {
    constructor({ resourceType, lookupParam, lookupValue, functionName, metadata, logLevel, }) {
        const message = `Unable to find [resource:${resourceType}] by [param:${lookupParam}] [value:${lookupValue}] [function:${functionName ?? 'n/a'}]`;
        const externalMessage = `Unable to find the requested ${resourceType}`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.NotFound = NotFound;
/**
 * Placeholder for authentication error, similar terminology to HTTP code
 * constructor params still needs some designing
 */
class Unauthorized extends FinleyError {
    constructor({ message, metadata, logLevel, }) {
        super(message, { metadata, logLevel });
    }
}
exports.Unauthorized = Unauthorized;
/**
 * Placeholder for typeguard error, similar terminology to HTTP code
 * constructor params still needs some designing
 */
class TypeError extends FinleyError {
    constructor({ resourceType, typeguardName, metadata, logLevel, }) {
        const message = `Resource type [${resourceType}] is invalid type, failing typeguard check [${typeguardName}]`;
        const externalMessage = `Unable to parse the ${resourceType} type`;
        super(message, { externalMessage, metadata, logLevel });
    }
}
exports.TypeError = TypeError;
// NOTE: assertion method will be exposed directly, to make TS compiler happy
const finleyAssert = (condition, err) => {
    if (condition) {
        return;
    }
    // eslint-disable-next-line no-console
    console.trace('Assertion failed:', err);
    const assertError = err instanceof Error ? err : new FinleyError(err);
    throw assertError;
};
exports.finleyAssert = finleyAssert;
// this compile time assert ensures that a function is exhaustive
// useful to use in your default case for switch statement.
const finleyAssertUnreachable = (x, err) => {
    (0, exports.finleyAssert)(false, err ?? `reached Unreachable ${x}`);
};
exports.finleyAssertUnreachable = finleyAssertUnreachable;
/**
 * see if this is a valid error for our error handling
 * @param err potential error
 * @returns boolean true if typeof error
 */
const isErrorType = (err) => {
    return err instanceof Error || err instanceof assert_1.default.AssertionError;
};
exports.isErrorType = isErrorType;
/**
 * see if this is a valid finley error for our error handling
 * @param err potential error
 * @returns boolean true if typeof error
 */
const isFinleyErrorType = (err) => {
    return err instanceof FinleyError;
};
exports.isFinleyErrorType = isFinleyErrorType;
const getErrorMessage = (err, defaultMessage) => {
    const message = (0, exports.isErrorType)(err)
        ? err.message ?? 'Unknown error occurred.'
        : defaultMessage ?? 'Unknown error occurred.';
    return message;
};
exports.getErrorMessage = getErrorMessage;
const generateFileNameForMessage = (message) => {
    // to make a filename from the message
    // replace all non-alphanumeric characters with underscores
    // and make it lowercase
    // limit the length to 30 characters
    // and add a timestamp and csv extension
    return message
        .replace(/[^a-zA-Z0-9]/g, '_')
        .toLowerCase()
        .slice(0, 30)
        .concat(`_${Date.now()}.csv`);
};
// generates an arr
const generateCSVForStructuredError = (error) => {
    const filename = generateFileNameForMessage(error.externalMessage);
    if (!error.tableData) {
        // just return single row with message
        return {
            filename,
            csvRows: [{ Message: error.externalMessage }],
            Message: error.externalMessage,
        };
    }
    const rows = [];
    const fieldSet = new Set();
    // Step 1: Collect all unique fields from tableData
    fieldSet.add('Message'); // Always include the 'Message' field
    error.tableData.rows.forEach((row) => {
        Object.keys(row).forEach((key) => fieldSet.add(key));
    });
    // Step 2: Build rows
    const baseRow = { Message: error.externalMessage };
    // Add rows from tableData
    error.tableData.rows.forEach((row) => {
        const fullRow = { ...baseRow };
        // Ensure all fields are included, even if empty
        fieldSet.forEach((field) => {
            fullRow[field] = row[field] ?? baseRow[field] ?? undefined;
        });
        rows.push(fullRow);
    });
    return {
        filename,
        csvRows: rows,
        Message: error.externalMessage,
    };
};
exports.generateCSVForStructuredError = generateCSVForStructuredError;
const DEFAULT_EXTERNAL_MESSAGE = 'An unexpected error occurred. Contact Finley support for help.';
const internalMessageToSerializedStructuredPublicError = ({ internalErrorMessage, externalErrorMessage, }) => ({
    externalMessage: externalErrorMessage ?? DEFAULT_EXTERNAL_MESSAGE,
    internalProperties: {
        internalErrorMessage,
    },
});
exports.internalMessageToSerializedStructuredPublicError = internalMessageToSerializedStructuredPublicError;
const MAX_LOG_META_LENGTH = 10_000;
const toStructuredErrors = (error) => {
    // If we already have structured errors, just pass them through.
    if (error instanceof StructuredPublicErrors) {
        return error.metadata.structuredErrors;
    }
    // It's a quirk of `FinleyError` that if `externalMessage` is not specified explicitly, it
    // defaults to the internal error message. This may cause sensitive information to leak, so we'll
    // only surface `externalMessage` if it differs from the internal one (i.e. if we know the creator
    // of the error explicitly specified `externalMessage`). Failing that, we'll fall back to a
    // generic message.
    const internalProperties = {};
    const externalMessage = error instanceof FinleyError && error.externalMessage !== error.message
        ? error.externalMessage
        : DEFAULT_EXTERNAL_MESSAGE;
    // If we're using a generic error message, surface the actual error message as an internal
    // property.
    if (externalMessage === DEFAULT_EXTERNAL_MESSAGE) {
        internalProperties.internalErrorMessage = (0, exports.getErrorMessage)(error);
    }
    // Copy any available metadata to `internalProperties`.
    if (error instanceof FinleyError) {
        for (const [key, value] of Object.entries(error.metadata?.logMeta ?? {})) {
            let valueString = typeof value === 'string' ? value : JSON.stringify(value);
            if (valueString.length > MAX_LOG_META_LENGTH) {
                valueString = `${valueString.substring(0, MAX_LOG_META_LENGTH)}... (truncated)`;
            }
            internalProperties[key] = valueString;
        }
    }
    if (error instanceof Error) {
        internalProperties.errorStack = error.stack || 'undefined';
    }
    return [{ externalMessage, internalProperties }];
};
exports.toStructuredErrors = toStructuredErrors;
//# sourceMappingURL=error.js.map